import React, { useState, useEffect, useMemo, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CDialog from "../../components/CDialog";
import CButton from "../../components/CButton";
import { Slider } from "@mui/material";
import { POPULATION_GROUPS, TRAINING_CATEGORY_OPTIONS } from "../../constant/constants";
import { supabase } from "../../config/supabaseClient";
import { getLocalData, formatZAR } from "../../constant/utils/commonUtils";
import { isUnemployedEmploymentType } from "../../constant/utils/employmentTypeUtils";
import { hasUnemployedTypesConfigured } from "../../constant/utils/dashboardUnemployedUtils";
import {
  getDisabilityConfig,
  resolveDisability,
} from "../../constant/utils/disabilityStatusUtils";
import { buildPlanOptimizerPayload } from "../../utils/planOptimizerService";
import { useSelector, shallowEqual } from "react-redux";


const AllocationSummaryModal = ({
  open,
  onClose,
  onReconfigure = null, // Optional - only needed in edit mode
  onSave = null, // Optional - only needed in edit mode
  generatedPlan,
  inputData,
  loading = false,
  // Slider configuration values from PlanOptimizerModal
  bbeeValues = {},
  trainingCategoryValues = {},
  disabilityData: disabilitySliderData = {},
  unemployedData: unemployedSliderData = {},
  bbeeType: bbeeTypeProp,
  trainingCategoryType: trainingCategoryTypeProp,
  totalBudget = 0,
  onSummaryCalculated, // Callback to pass calculated summary data
  // Read-only mode props (for viewing saved summaries)
  readOnly = false,
  storedSummary = null, // Pre-calculated summary data from database
  criticalSkillsPriority = null, // Critical Skills Priority value for display
}) => {

  const companyId = getLocalData("sdfUser")?.company_id;
  const { setaTemplate, disabilityTypes, employmentTypes } = useSelector(
    (state) => ({
      setaTemplate: state.setaTemplate.setaTemplate,
      disabilityTypes: state.setaTemplate.disabilityTypes || [],
      employmentTypes: state.setaTemplate.employmentTypes || [],
    }),
    shallowEqual,
  );
  const disabilityConfig = useMemo(
    () => getDisabilityConfig({ disabilityTypes, fields: setaTemplate }),
    [disabilityTypes, setaTemplate],
  );
  const [calculatedBBEECurrent, setCalculatedBBEECurrent] = useState({});
  const [calculatedTrainingCategoryCurrent, setCalculatedTrainingCategoryCurrent] = useState({});

  // Use totalBudget prop for budget total
  const budget = { total: totalBudget || 0, allocated: 0, percentage: 0 };

  // Get BBEE type from prop or inputData to determine if we calculate budget or headcount
  const bbeeType = useMemo(() => {
    if (bbeeTypeProp) {
      return bbeeTypeProp.toLowerCase();
    }
    if (inputData?.optimizer_config?.allocation_type[0]) {
      return inputData?.optimizer_config?.allocation_type[0]?.toLowerCase();
    }
    // Fallback: check if current values look like budget (large numbers) or headcount (small numbers)
    // Default to "budget" if cannot determine
    return "budget";
  }, [bbeeTypeProp, inputData]);

  // Get Training Category type from prop or inputData (may differ from BBEE type)
  const trainingCategoryType = useMemo(() => {
    if (trainingCategoryTypeProp) {
      return trainingCategoryTypeProp.toLowerCase();
    }
    if (inputData?.optimizer_config?.allocation_type[1]) {
      return inputData?.optimizer_config?.allocation_type[1]?.toLowerCase();
    }
    // Default to "budget" if cannot determine
    return "budget";
  }, [trainingCategoryTypeProp, inputData]);

  const isBudgetType = bbeeType === "budget";
  const isTrainingCategoryBudgetType = trainingCategoryType === "budget";

  // Calculate current values for BBEE segments based on generatedPlan
  useEffect(() => {
    const calculateBBEECurrentValues = async () => {
      if (storedSummary) {
        // Still allow live calc when summary lacks unemployed (older edge deploys)
        if (storedSummary.unemployed != null) {
          return;
        }
      }

      if (!generatedPlan?.employees || !companyId) {
        setCalculatedBBEECurrent({});
        return;
      }

      try {
        const employees = generatedPlan.employees || [];

        // Extract all unique emp_ids and training_ids
        const empIds = employees.map(emp => emp.emp_id).filter(Boolean);
        const allTrainingIds = employees.flatMap(emp => emp.training_ids || []).filter(Boolean);

        if (empIds.length === 0) {
          setCalculatedBBEECurrent({});
          return;
        }

        // Check if emp_ids are UUIDs or text emp_ids
        const isUUID = (str) => {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          return uuidRegex.test(str);
        };

        const usingUUID = empIds.length > 0 && isUUID(empIds[0]);

        // Fetch employee data (race, gender, disability_status, employment_type, stipend_amount for unemployed cost)
        let employeeData = [];
        if (usingUUID) {
          const { data, error } = await supabase
            .from("company_user")
            .select("id, emp_id, race, gender, disability_status, employment_type, stipend_amount, data")
            .eq("company_id", companyId)
            .in("id", empIds)
            .neq("status", "archived");

          if (error) {
            console.error("Error fetching employee data:", error);
            return;
          }
          employeeData = data || [];
        } else {
          const { data, error } = await supabase
            .from("company_user")
            .select("id, emp_id, race, gender, disability_status, employment_type, stipend_amount, data")
            .eq("company_id", companyId)
            .in("emp_id", empIds)
            .neq("status", "archived");

          if (error) {
            console.error("Error fetching employee data:", error);
            return;
          }
          employeeData = data || [];
        }

        // Create a map of emp_id to employee data
        const employeeMap = new Map();
        employeeData.forEach(emp => {
          const key = usingUUID ? emp.id : emp.emp_id;
          employeeMap.set(key, emp);
        });

        // Fetch training costs
        let trainingCostMap = new Map();
        if (allTrainingIds.length > 0) {
          let trainingQuery = supabase
            .from("training_catalogue_data")
            .select("id, cost")
            .in("id", allTrainingIds);

          if (!readOnly) {
            trainingQuery = trainingQuery.eq("status", "active");
          } else {
            trainingQuery = trainingQuery.neq("status", "archived");
          }

          const { data: trainingsData, error: trainingsError } = await trainingQuery;

          if (!trainingsError && trainingsData) {
            trainingsData.forEach(training => {
              trainingCostMap.set(training.id, Number(training.cost) || 0);
            });
          }
        }

        // Extract training categories from inputData (optimizedPayload) instead of querying      
        let trainingCategoryList = [];
        if (inputData?.employees && Array.isArray(inputData.employees)) {
          inputData.employees.forEach(emp => {
            const empId = emp.emp_id;
            if (!empId) return;

            if (emp.training_selected && Array.isArray(emp.training_selected)) {
              emp.training_selected.forEach((training) => {
                // Support object shape (v2) and legacy [training_id, category] tuples
                const trainingId =
                  training && typeof training === "object" && !Array.isArray(training)
                    ? training.training_id
                    : training?.[0];
                const trainingCategory =
                  training && typeof training === "object" && !Array.isArray(training)
                    ? training.training_category
                    : training?.[1];
                const trainingCost =
                  training && typeof training === "object" && !Array.isArray(training)
                    && training.cost != null
                    ? Number(training.cost) || 0
                    : null;
                if (trainingId && trainingCategory) {
                  trainingCategoryList.push({
                    emp_id: empId,
                    training_id: trainingId,
                    category: trainingCategory,
                    cost: trainingCost,
                  });
                }
              });
            }
          });
        }
        // Calculate current values grouped by race/gender and training categories in a single loop
        const calculated = {};
        const calculatedCategories = {};

        employees.forEach(empPlan => {
          const empId = empPlan.emp_id;
          const trainingIds = empPlan.training_ids || [];

          // Skip if employee has no trainings
          if (trainingIds.length === 0) return;

          // Find employee data
          const employee = employeeMap.get(empId);
          if (!employee) return;

          const race = (employee.race || "").toLowerCase();
          const gender = (employee.gender || "").toLowerCase();
          const hasDisability = resolveDisability({
            record: employee,
            disabilityOptions: disabilityConfig.disabilityOptions,
            disabilityFieldName: disabilityConfig.disabilityFieldName,
          });
          const isUnemployed = isUnemployedEmploymentType(
            employmentTypes,
            employee?.employment_type,
          );
          const stipendAmount = Number(empPlan.stipend_amount ?? employee?.stipend_amount ?? 0) || 0;

          // Prefer per-employee training cost from inputData (same training_id can differ by learner)
          const inputEmp = (inputData?.employees || []).find(
            (e) => String(e.emp_id) === String(empId),
          );
          const resolveTrainingCost = (trainingId) => {
            const selected = (inputEmp?.training_selected || []).find((t) => {
              if (!t) return false;
              if (typeof t === "object" && !Array.isArray(t)) {
                return String(t.training_id) === String(trainingId);
              }
              return String(t[0]) === String(trainingId);
            });
            if (
              selected &&
              typeof selected === "object" &&
              !Array.isArray(selected) &&
              selected.cost != null
            ) {
              return Number(selected.cost) || 0;
            }
            return trainingCostMap.get(trainingId) || 0;
          };

          // Payload already includes stipend on first unemployed training cost when present
          let employeeTotalCost = trainingIds.reduce((sum, trainingId) => {
            return sum + resolveTrainingCost(trainingId);
          }, 0);
          const costsFromPayload = trainingIds.some((tid) => {
            const selected = (inputEmp?.training_selected || []).find((t) =>
              t && typeof t === "object" && !Array.isArray(t)
                ? String(t.training_id) === String(tid)
                : false,
            );
            return selected?.cost != null;
          });
          if (isUnemployed && !costsFromPayload) employeeTotalCost += stipendAmount;

          // Unemployed: only count toward unemployed pool (not race/gender or disability)
          if (isUnemployed) {
            if (!calculated.unemployed) {
              calculated.unemployed = { total: { current: 0 } };
            }
            if (isBudgetType) {
              calculated.unemployed.total.current =
                (calculated.unemployed.total.current || 0) + employeeTotalCost;
            } else {
              calculated.unemployed.total.current =
                (calculated.unemployed.total.current || 0) + 1;
            }
          }

          // Handle disability FIRST (total only, regardless of gender or race)
          // This ensures employees with disability are counted even if race/gender is missing
          if (hasDisability && !isUnemployed) {
            if (!calculated.disability) {
              calculated.disability = { total: { current: 0 } };
            }
            if (isBudgetType) {
              // Sum costs for budget type
              calculated.disability.total.current = (calculated.disability.total.current || 0) + employeeTotalCost;
            } else {
              // Count employee headcount for headcount type (each employee with disability = 1)
              calculated.disability.total.current = (calculated.disability.total.current || 0) + 1;
            }
          }

          // Process training categories for this employee
          trainingIds.forEach((trainingId, trainingIndex) => {
            // Find matching entry in trainingCategoryList for this emp_id and training_id
            const categoryEntry = trainingCategoryList.find(
              item => item.emp_id === empId && item.training_id === trainingId
            );

            if (categoryEntry && categoryEntry.category) {
              const category = categoryEntry.category;
              // Prefer per-employee cost from input; stipend already in payload cost when present
              let cost =
                categoryEntry.cost != null
                  ? Number(categoryEntry.cost) || 0
                  : resolveTrainingCost(trainingId);
              if (
                isTrainingCategoryBudgetType &&
                isUnemployed &&
                trainingIndex === 0 &&
                categoryEntry.cost == null &&
                !costsFromPayload
              ) {
                cost += stipendAmount;
              }

              // Initialize category if not exists
              if (!calculatedCategories[category]) {
                calculatedCategories[category] = { current: 0 };
              }

              // Add to category total
              if (isTrainingCategoryBudgetType) {
                // Sum costs for budget type
                calculatedCategories[category].current = (calculatedCategories[category].current || 0) + cost;
              } else {
                // Count headcount for headcount type (each training counts as 1)
                calculatedCategories[category].current = (calculatedCategories[category].current || 0) + 1;
              }
            }
          });

          // Skip race/gender calculation if unemployed, or race or gender is missing
          if (isUnemployed || !race || !gender) return;

          // Initialize race group if not exists
          if (!calculated[race]) {
            calculated[race] = {
              male: { current: 0 },
              female: { current: 0 }
            };
          }

          // Add to appropriate gender bucket
          if (isBudgetType) {
            // Sum costs
            if (gender === "male") {
              calculated[race].male.current = (calculated[race].male.current || 0) + employeeTotalCost;
            } else if (gender === "female") {
              calculated[race].female.current = (calculated[race].female.current || 0) + employeeTotalCost;
            }
          } else {
            // Count headcount (each employee counts as 1)
            if (gender === "male") {
              calculated[race].male.current = (calculated[race].male.current || 0) + 1;
            } else if (gender === "female") {
              calculated[race].female.current = (calculated[race].female.current || 0) + 1;
            }
          }
        });

        setCalculatedBBEECurrent(calculated);
        setCalculatedTrainingCategoryCurrent(calculatedCategories);
      } catch (error) {
        console.error("Error calculating BBEE current values:", error);
        setCalculatedBBEECurrent({});
      }
    };

    calculateBBEECurrentValues();
  }, [readOnly, storedSummary, generatedPlan, companyId, isBudgetType, isTrainingCategoryBudgetType, inputData, disabilityConfig, employmentTypes]);

  // Format numbers with commas (for non-budget values like headcount)
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return Number(num).toLocaleString('en-ZA');
  };

  // Helper function to capitalize first letter
  const capitalizeFirst = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Get BBEE segments with male/female bifurcation
  const getBBEESegments = () => {
    const segments = [];

    // Get the order from POPULATION_GROUPS constant (dynamic)
    const segmentOrder = POPULATION_GROUPS.map(group => group.key.toLowerCase());
    const sourceKeys = Object.keys(bbeeValues);

    // Process segments in order, prioritizing POPULATION_GROUPS order
    segmentOrder.forEach((key) => {
      // Check both lowercase and capitalized versions of the key
      const keyCapitalized = capitalizeFirst(key);
      const keyExists = sourceKeys.includes(key) || sourceKeys.includes(keyCapitalized);

      if (keyExists && key !== "disability") {
        // Use the actual key from sourceKeys (might be capitalized or lowercase)
        const actualKey = sourceKeys.includes(key) ? key : keyCapitalized;
        // Find the label from POPULATION_GROUPS if available
        const groupInfo = POPULATION_GROUPS.find(g => g.key.toLowerCase() === key.toLowerCase());
        const segmentName = groupInfo?.label || capitalizeFirst(key);

        // Get target values from slider config (current values as target in allocation summary)
        const maleTarget = Number(bbeeValues[actualKey]?.male?.c || 0);
        const femaleTarget = Number(bbeeValues[actualKey]?.female?.c || 0);

        // Use only calculated current values (from generatedPlan)
        // Use lowercase key for calculated values as they're stored lowercase
        const calculatedMale = calculatedBBEECurrent[key]?.male?.current ?? 0;
        const calculatedFemale = calculatedBBEECurrent[key]?.female?.current ?? 0;

        segments.push({
          name: segmentName,
          key: key,
          male: {
            target: maleTarget,
            current: calculatedMale,
          },
          female: {
            target: femaleTarget,
            current: calculatedFemale,
          },
        });
      }
    });

    return segments;
  };

  // Get Disability data (total only, no male/female split)
  const getDisabilityData = () => {
    // Use only calculated current value (from generatedPlan)
    const calculatedCurrent = calculatedBBEECurrent.disability?.total?.current ?? 0;

    // Use slider configuration for target value
    if (disabilitySliderData && disabilitySliderData.total !== undefined && disabilitySliderData.total !== null) {
      const targetValue = typeof disabilitySliderData.total === 'string'
        ? parseFloat(disabilitySliderData.total) || 0
        : (disabilitySliderData.total || 0);

      return {
        target: targetValue,
        current: calculatedCurrent,
      };
    }

    return null;
  };

  // Unemployed: single total (part of BBEE Current Plan)
  const getUnemployedData = () => {
    if (!hasUnemployedTypesConfigured(employmentTypes)) return null;
    const calculatedCurrent =
      calculatedBBEECurrent.unemployed?.total?.current ?? 0;
    if (
      unemployedSliderData &&
      (unemployedSliderData.c !== undefined ||
        unemployedSliderData.total !== undefined)
    ) {
      const targetValue =
        unemployedSliderData.c != null
          ? Number(unemployedSliderData.c) || 0
          : typeof unemployedSliderData.total === "string"
            ? parseFloat(unemployedSliderData.total) || 0
            : Number(unemployedSliderData.total) || 0;
      return { target: targetValue, current: calculatedCurrent };
    }
    if (calculatedCurrent > 0) {
      return { target: 0, current: calculatedCurrent };
    }
    return null;
  };

  // Get training categories
  const getTrainingCategories = () => {
    const categories = [];

    // Use TRAINING_CATEGORY_OPTIONS to maintain order and get all categories
    TRAINING_CATEGORY_OPTIONS.forEach((categoryOption) => {
      const key = categoryOption.value;
      const sliderValue = trainingCategoryValues[key];

      if (sliderValue) {
        // Use slider configuration values (current values as target in allocation summary)
        const targetValue = Number(sliderValue.c || 0);
        const calculatedCurrent = calculatedTrainingCategoryCurrent[key]?.current ?? 0;

        categories.push({
          name: key,
          target: targetValue,
          current: calculatedCurrent,
        });
      }
    });

    return categories;
  };

  // Use stored summary when precomputed (v2) or in read-only mode
  const useStoredSummary = Boolean(storedSummary);

  const bbeeSegments = useMemo(() => {
    return useStoredSummary
      ? (storedSummary.bbee_segments || [])
      : getBBEESegments();
  }, [readOnly, storedSummary, bbeeValues, calculatedBBEECurrent]);

  const disabilityData = useMemo(() => {
    if (useStoredSummary && storedSummary?.disability != null) {
      return storedSummary.disability;
    }
    return getDisabilityData();
  }, [readOnly, storedSummary, disabilitySliderData, calculatedBBEECurrent]);

  const unemployedData = useMemo(() => {
    if (useStoredSummary && storedSummary?.unemployed != null) {
      return storedSummary.unemployed;
    }
    return getUnemployedData();
  }, [readOnly, storedSummary, unemployedSliderData, calculatedBBEECurrent, employmentTypes]);

  const trainingCategories = useMemo(() => {
    return useStoredSummary
      ? (storedSummary.training_categories || [])
      : getTrainingCategories();
  }, [readOnly, storedSummary, trainingCategoryValues, calculatedTrainingCategoryCurrent]);

  // Calculate BBEE totals
  const bbeeTotals = useMemo(() => {
    return useStoredSummary
      ? (storedSummary.bbee_totals || { maleTarget: 0, maleCurrent: 0, femaleTarget: 0, femaleCurrent: 0 })
      : bbeeSegments.reduce((acc, segment) => {
        acc.maleTarget += segment.male.target || 0;
        acc.maleCurrent += segment.male.current || 0;
        acc.femaleTarget += segment.female.target || 0;
        acc.femaleCurrent += segment.female.current || 0;
        return acc;
      }, { maleTarget: 0, maleCurrent: 0, femaleTarget: 0, femaleCurrent: 0 });
  }, [readOnly, storedSummary, bbeeSegments]);

  // Calculate total allocated from BBEE breakdown (race-gender + unemployed; disability is overlay-only)
  const totalAllocated = useMemo(() => {
    const raceGender =
      (bbeeTotals.maleCurrent || 0) + (bbeeTotals.femaleCurrent || 0);
    const unemployedCurrent = Number(unemployedData?.current) || 0;
    return useStoredSummary
      ? (storedSummary.budget?.allocated ||
          storedSummary.bbee_totals?.planCurrent ||
          raceGender + unemployedCurrent)
      : raceGender + unemployedCurrent;
  }, [readOnly, storedSummary, bbeeTotals, unemployedData]);

  // Calculate percentage for slider (can exceed 100% for over-allocation)
  const budgetPercentage = useMemo(() => {
    return useStoredSummary
      ? (storedSummary.budget?.percentage || 0)
      : (budget.total > 0
        ? Math.max(0, (totalAllocated / budget.total) * 100)
        : 0);
  }, [readOnly, storedSummary, totalAllocated, budget.total]);

  // Update budget from stored summary if available
  const displayBudget = useStoredSummary && storedSummary.budget
    ? storedSummary.budget
    : { total: budget.total || 0, allocated: totalAllocated, percentage: budgetPercentage };

  // Check if over-allocated
  const isOverAllocated = useMemo(() => {
    const percentage = displayBudget.percentage || budgetPercentage;
    return percentage > 100;
  }, [displayBudget, budgetPercentage]);

  // Calculate dynamic max for slider (always 100% - when over-allocated, thumb goes to end)
  const sliderMax = 100;

  // Calculate Training Category totals
  const trainingCategoryTotals = useMemo(() => {
    return useStoredSummary
      ? (storedSummary.training_category_totals || { target: 0, current: 0 })
      : trainingCategories.reduce((acc, category) => {
        acc.target += category.target || 0;
        acc.current += category.current || 0;
        return acc;
      }, { target: 0, current: 0 });
  }, [readOnly, storedSummary, trainingCategories]);

  // Calculate Black Total Target and Current (sum of African + Coloured + Indian)
  const blackTotal = useMemo(() => {
    const blackGroups = POPULATION_GROUPS.filter((group) => group.category === "black");
    let targetTotal = 0;
    let currentTotal = 0;
    blackGroups.forEach((group) => {
      const segment = bbeeSegments.find((s) => s.key === group.key.toLowerCase());
      if (segment) {
        targetTotal += (segment.male.target || 0) + (segment.female.target || 0);
        currentTotal += (segment.male.current || 0) + (segment.female.current || 0);
      }
    });
    return { target: targetTotal, current: currentTotal };
  }, [bbeeSegments]);

  // Calculate Other Total Target and Current (sum of White + Foreigner)
  const otherTotal = useMemo(() => {
    const otherGroups = POPULATION_GROUPS.filter((group) => group.category === "other");
    let targetTotal = 0;
    let currentTotal = 0;
    otherGroups.forEach((group) => {
      const segment = bbeeSegments.find((s) => s.key === group.key.toLowerCase());
      if (segment) {
        targetTotal += (segment.male.target || 0) + (segment.female.target || 0);
        currentTotal += (segment.male.current || 0) + (segment.female.current || 0);
      }
    });
    return { target: targetTotal, current: currentTotal };
  }, [bbeeSegments]);

  const blackGroupLabels = POPULATION_GROUPS
    .filter((group) => group.category === "black")
    .map((group) => group.label)
    .join(" + ");

  const otherGroupLabels = POPULATION_GROUPS
    .filter((group) => group.category === "other")
    .map((group) => group.label)
    .join(" + ");

  const formatSummaryValue = (value) =>
    isBudgetType ? formatZAR(value) : formatNumber(value);

  // Get critical skills priority from prop, storedSummary, or inputData
  const displayCriticalSkillsPriority = useMemo(() => {
    // First check prop (passed from parent)
    if (criticalSkillsPriority !== null && criticalSkillsPriority !== undefined) {
      return criticalSkillsPriority;
    }
    // Check stored summary (for read-only mode)
    if (readOnly && storedSummary?.critical_skills_priority !== undefined) {
      return storedSummary.critical_skills_priority;
    }
    // Try to extract from inputData
    if (inputData?.optimizer_config?.priority_weights?.critical_skills !== undefined) {
      return inputData.optimizer_config.priority_weights.critical_skills;
    }
    return null;
  }, [criticalSkillsPriority, readOnly, storedSummary, inputData]);

  // Use refs to track previous summary data and callback to prevent infinite loops
  const prevSummaryDataRef = useRef(null);
  const onSummaryCalculatedRef = useRef(onSummaryCalculated);

  // Update ref when callback changes
  useEffect(() => {
    onSummaryCalculatedRef.current = onSummaryCalculated;
  }, [onSummaryCalculated]);

  // Calculate and pass summary data to parent when ready (only in edit mode)
  useEffect(() => {
    if (!readOnly && onSummaryCalculatedRef.current && bbeeSegments.length > 0) {
      const summaryData = {
        budget: {
          total: budget.total || 0,
          allocated: totalAllocated,
          percentage: budgetPercentage,
        },
        bbee_segments: bbeeSegments,
        disability: disabilityData,
        unemployed: unemployedData,
        training_categories: trainingCategories,
        bbee_totals: bbeeTotals,
        training_category_totals: trainingCategoryTotals,
        critical_skills_priority: displayCriticalSkillsPriority,
      };

      // Only call onSummaryCalculated if data has actually changed
      const summaryDataString = JSON.stringify(summaryData);
      if (prevSummaryDataRef.current !== summaryDataString) {
        prevSummaryDataRef.current = summaryDataString;
        onSummaryCalculatedRef.current(summaryData);
      }
    }
  }, [readOnly, bbeeSegments, disabilityData, unemployedData, trainingCategories, bbeeTotals, trainingCategoryTotals, totalAllocated, budgetPercentage, budget.total, displayCriticalSkillsPriority]);

  return (
    <CDialog
      open={open}
      onClose={onClose}
      className="allocation-summary-dialog"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "calc(100% - 32px)",
          maxWidth: 1200,
          maxHeight: "calc(100vh - 32px)",
          m: 2,
          p: "0 !important",
          overflow: "hidden",
        },
      }}
    >
      <div className="w-full min-w-0 max-h-[calc(100vh-64px)] overflow-y-auto bg-white rounded-lg p-4 md:p-6">
        {/* Modal Header */}
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-blue-500">Allocation Summary</h2>
          <CButton
            onClick={onClose}
            className="!p-0 !min-w-0 hover:!bg-transparent shrink-0"
          >
            <CloseIcon className="text-gray-700 hover:text-gray-900 w-6 h-6" />
          </CButton>
        </div>

        <div className="border border-blue-200 rounded-lg p-2 md:p-3">
          {/* Total Budget vs Allocated Budget Section */}
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
              <h3 className="text-base font-semibold text-gray-800">
                Total Budget vs Allocated Budget
              </h3>
              {/* Critical Skills Priority Display - Top Right */}
              {displayCriticalSkillsPriority !== null && displayCriticalSkillsPriority !== undefined && (
                <div className="border border-blue-200 rounded-lg px-3 py-2 bg-gray-50 shrink-0 self-start">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Critical Skills Priority:</span>
                    <span className="text-base font-semibold text-blue-500">{displayCriticalSkillsPriority}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              {/* Progress Bar Container */}
              <div className="relative w-full h-16 rounded-lg px-4 flex items-center">
                {/* Tick marks - positioned at 0, 20, 40, 60, 80, 100 */}
                <div className="absolute inset-0 flex justify-between items-center px-4">
                  {[0, 20, 40, 60, 80, 100].map((tick) => (
                    <div
                      key={tick}
                      className="w-0.5 h-3 bg-gray-300"
                    />
                  ))}
                </div>

                {/* Slider */}
                <div className="relative w-full">
                  <Slider
                    value={isOverAllocated ? 100 : Math.min(displayBudget.percentage || budgetPercentage, 100)}
                    min={0}
                    max={100}
                    step={0.01}
                    disabled
                    sx={{
                      width: '100%',
                      height: 8,
                      '& .MuiSlider-thumb': {
                        width: 24,
                        height: 24,
                        backgroundColor: isOverAllocated ? '#dc2626' : '#008cd1',
                        border: '3px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: isOverAllocated ? '#dc2626' : '#008cd1',
                        height: 8,
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: '#e5e7eb',
                        height: 8,
                      },
                    }}
                  />

                  {/* Percentage Label - positioned above the thumb */}
                  <div
                    className={`absolute ${isOverAllocated ? 'bg-red-500' : 'bg-blue-500'} text-white px-3 py-1 rounded text-sm font-semibold whitespace-nowrap`}
                    style={{
                      left: (() => {
                        // When over-allocated, position at the end (100%), otherwise use actual position
                        if (isOverAllocated) {
                          return 'calc(100% - 50px)';
                        }
                        const percentage = Math.min(displayBudget.percentage || budgetPercentage, 100);
                        return `calc(${percentage}% - 50px)`;
                      })(),
                      top: '-35px',
                      minWidth: '100px',
                      textAlign: 'center',
                      transform: (() => {
                        // Adjust transform based on position
                        if (isOverAllocated) return 'translateX(-50px)';
                        const percentage = Math.min(displayBudget.percentage || budgetPercentage, 100);
                        if (percentage > 90) return 'translateX(-50px)';
                        if (percentage < 10) return 'translateX(0)';
                        return 'translateX(-50px)';
                      })(),
                    }}
                  >
                    {(displayBudget.percentage || budgetPercentage).toFixed(2)}% {isOverAllocated ? 'Over-allocated' : 'Allocated'}
                  </div>
                </div>
              </div>

              {/* Budget Values */}
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 mt-2 text-sm text-gray-600">
                <span>Total: {formatZAR(displayBudget.total)}</span>
                <span>Allocated: {formatZAR(displayBudget.allocated)}</span>
              </div>
            </div>
          </div>

          {/* Tables: stack until xl so B-BBEE never squeezes mid-width; side-by-side from 1280px+ */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4 items-stretch">
            {/* Breakdown by BBEE Section */}
            <div className="flex flex-col min-h-0 min-w-0">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Breakdown by B-BBEE
              </h3>
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="text-left border-b border-blue-200 font-semibold text-blue-500 text-sm p-3 whitespace-nowrap">
                        Segment
                      </th>
                      <th className="text-center border-b border-blue-200 font-semibold text-sm p-3" colSpan="2">
                        Male
                      </th>
                      <th className="text-center border-b border-blue-200 font-semibold text-sm p-3" colSpan="2">
                        Female
                      </th>
                    </tr>
                    <tr className="bg-blue-100">
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3">
                      </th>
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3 whitespace-nowrap">
                        Target (T)
                      </th>
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3 whitespace-nowrap">
                        Current (C)
                      </th>
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3 whitespace-nowrap">
                        Target (T)
                      </th>
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3 whitespace-nowrap">
                        Current (C)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bbeeSegments.map((segment, index) => (
                      <tr
                        key={segment.name}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700 whitespace-nowrap">
                          {segment.name}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700 whitespace-nowrap">
                          {isBudgetType ? formatZAR(segment.male.target) : formatNumber(segment.male.target)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700 whitespace-nowrap">
                          {isBudgetType ? formatZAR(segment.male.current) : formatNumber(segment.male.current)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700 whitespace-nowrap">
                          {isBudgetType ? formatZAR(segment.female.target) : formatNumber(segment.female.target)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700 whitespace-nowrap">
                          {isBudgetType ? formatZAR(segment.female.current) : formatNumber(segment.female.current)}
                        </td>
                      </tr>
                    ))}
                    {bbeeSegments.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-3 text-center text-sm text-gray-500">
                          No B-BBEE data available
                        </td>
                      </tr>
                    )}
                    {/* Total Row */}
                    {bbeeSegments.length > 0 && (
                      <tr className="bg-blue-50 font-semibold">
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800 whitespace-nowrap">
                          Total
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800 whitespace-nowrap">
                          {isBudgetType ? formatZAR(bbeeTotals.maleTarget) : formatNumber(bbeeTotals.maleTarget)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800 whitespace-nowrap">
                          {isBudgetType ? formatZAR(bbeeTotals.maleCurrent) : formatNumber(bbeeTotals.maleCurrent)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800 whitespace-nowrap">
                          {isBudgetType ? formatZAR(bbeeTotals.femaleTarget) : formatNumber(bbeeTotals.femaleTarget)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800 whitespace-nowrap">
                          {isBudgetType ? formatZAR(bbeeTotals.femaleCurrent) : formatNumber(bbeeTotals.femaleCurrent)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Breakdown by Training Category Section */}
            <div className="flex flex-col min-h-0 min-w-0">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Breakdown by Training Category
              </h3>
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3 text-blue-500">
                        Category
                      </th>
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3">
                        Target (T)
                      </th>
                      <th className="text-left border-b border-blue-200 font-semibold text-sm p-3">
                        Current (C)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainingCategories.map((category, index) => (
                      <tr
                        key={category.name}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700">
                          {category.name}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700">
                          {isTrainingCategoryBudgetType ? formatZAR(category.target) : formatNumber(category.target)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-700">
                          {isTrainingCategoryBudgetType ? formatZAR(category.current) : formatNumber(category.current)}
                        </td>
                      </tr>
                    ))}
                    {trainingCategories.length === 0 && (
                      <tr>
                        <td colSpan="3" className="p-3 text-center text-sm text-gray-500">
                          No training category data available
                        </td>
                      </tr>
                    )}
                    {/* Total Row */}
                    {trainingCategories.length > 0 && (
                      <tr className="bg-blue-50 font-semibold">
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800">
                          Total
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800">
                          {isTrainingCategoryBudgetType ? formatZAR(trainingCategoryTotals.target) : formatNumber(trainingCategoryTotals.target)}
                        </td>
                        <td className="border-b border-blue-200 p-3 text-sm text-gray-800">
                          {isTrainingCategoryBudgetType ? formatZAR(trainingCategoryTotals.current) : formatNumber(trainingCategoryTotals.current)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Paired summary strip: BBEE rollups | Other allocations */}
          {(bbeeSegments.length > 0 || unemployedData || disabilityData) && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-2 overflow-x-hidden">
              {/* BBEE summaries */}
              {bbeeSegments.length > 0 ? (
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    B-BBEE summaries
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="border border-blue-200 rounded-lg overflow-hidden bg-white shadow-sm min-w-0">
                      <div className="bg-blue-100 px-2.5 sm:px-3 py-2 border-b border-blue-200 min-h-[52px]">
                        <div className="text-sm font-semibold text-blue-500">Black Total</div>
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5 break-words">{blackGroupLabels}</div>
                      </div>
                      <div className="px-2.5 sm:px-3 py-2.5 bg-gray-50 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-500 shrink-0">Target (T)</span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                            {formatSummaryValue(blackTotal.target)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-500 shrink-0">Current (C)</span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                            {formatSummaryValue(blackTotal.current)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="border border-blue-200 rounded-lg overflow-hidden bg-white shadow-sm min-w-0">
                      <div className="bg-blue-100 px-2.5 sm:px-3 py-2 border-b border-blue-200 min-h-[52px]">
                        <div className="text-sm font-semibold text-blue-500">Other Total</div>
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5 break-words">{otherGroupLabels}</div>
                      </div>
                      <div className="px-2.5 sm:px-3 py-2.5 bg-gray-50 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-500 shrink-0">Target (T)</span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                            {formatSummaryValue(otherTotal.target)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-500 shrink-0">Current (C)</span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                            {formatSummaryValue(otherTotal.current)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="hidden xl:block" aria-hidden="true" />
              )}

              {/* Other allocations */}
              {(unemployedData || disabilityData) ? (
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Other allocations
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {unemployedData && (
                      <div className="border border-blue-200 rounded-lg overflow-hidden bg-white shadow-sm min-w-0">
                        <div className="bg-blue-100 px-2.5 sm:px-3 py-2 border-b border-blue-200 min-h-[52px] flex items-center">
                          <div className="text-sm font-semibold text-blue-500">Unemployed</div>
                        </div>
                        <div className="px-2.5 sm:px-3 py-2.5 bg-gray-50 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-500 shrink-0">Target (T)</span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                              {formatSummaryValue(unemployedData.target)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-500 shrink-0">Current (C)</span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                              {formatSummaryValue(unemployedData.current)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {disabilityData && (
                      <div className="border border-blue-200 rounded-lg overflow-hidden bg-white shadow-sm min-w-0">
                        <div className="bg-blue-100 px-2.5 sm:px-3 py-2 border-b border-blue-200 min-h-[52px] flex items-center">
                          <div className="text-sm font-semibold text-blue-500">Disability</div>
                        </div>
                        <div className="px-2.5 sm:px-3 py-2.5 bg-gray-50 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-500 shrink-0">Target (T)</span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                              {formatSummaryValue(disabilityData.target)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-500 shrink-0">Current (C)</span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">
                              {formatSummaryValue(disabilityData.current)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="hidden xl:block" aria-hidden="true" />
              )}
            </div>
          )}
        </div>
        {/* Action Buttons - Hide in read-only mode */}
        {!readOnly && onSave && onReconfigure && (
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <CButton
              onClick={onSave}
              disabled={loading}
              className="bg-blue-500 text-white hover:bg-blue-600 border border-blue-500 rounded-lg px-5 sm:px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {loading ? "Saving..." : "Save"}
            </CButton>
            <CButton
              onClick={onReconfigure}
              className="bg-white text-blue-500 hover:bg-blue-50 border border-blue-500 rounded-lg px-5 sm:px-6 py-2 font-semibold"
            >
              Regenerate
            </CButton>
            <CButton
              onClick={onClose}
              className="bg-white text-blue-500 hover:bg-blue-50 border border-blue-500 rounded-lg px-5 sm:px-6 py-2 font-semibold"
            >
              Cancel
            </CButton>
          </div>
        )}
        {/* Close button for read-only mode */}
        {readOnly && (
          <div className="flex justify-center gap-3 pt-4 border-t border-gray-200">
            <CButton
              onClick={onClose}
              className="bg-blue-500 text-white hover:bg-blue-600 border border-blue-500 rounded-lg px-5 sm:px-6 py-2 font-semibold"
            >
              Close
            </CButton>
          </div>
        )}
      </div>
    </CDialog>
  );
};

export default AllocationSummaryModal;

